import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { RootState } from '../../../../store';
import {
  getUsersList,
  AdminUser,
  UserListResponse,
  ROLES,
  getRoleBadgeClass,
  updateUserActivationStatus,
  deleteUser,
  SubscriptionStatus
} from '../../../utils/adminApiUtils';
import { useI18n } from '../../../contexts/I18nContext';
import UserDialog from './UserDialog';
import TariffsManager from './TariffsManager';
import OrganizationsManager from './OrganizationsManager';
import CustomSelect from '../../../components/CustomSelect';
import FiltersBottomSheet from './FiltersBottomSheet';
import '../styles/AdminPage.scss';
import SpecialtiesManager from './SpecialtiesManager';
import AIFeedbackManager from './AIFeedbackManager';

const getSubscriptionStatusKey = (status: SubscriptionStatus): string => {
  const keys: Record<SubscriptionStatus, string> = {
    active: 'admin.subscription.active',
    canceled: 'admin.subscription.canceled',
    expired: 'admin.subscription.expired',
  };
  return keys[status] || 'admin.subscription.active';
};

const renderUserSubscriptionStatus = (user: AdminUser, t: (k: string) => string): React.ReactNode => {
  const status =
    user.active_subscription_status ??
    (user.has_active_subscription ? ('active' as SubscriptionStatus) : null);

  if (!status) {
    return <span className="status-dot inactive">{t('admin.subscription.none')}</span>;
  }

  const label = t(getSubscriptionStatusKey(status));

  return (
    <div className="subscription-status-cell">
      <span className={`status-dot ${status}`}>{label}</span>
      {user.active_subscription_next_billing_date && (
        <small>
          до {new Date(user.active_subscription_next_billing_date).toLocaleDateString('ru-RU')}
        </small>
      )}
    </div>
  );
};

type AdminTab = 'users' | 'tariffs' | 'organizations' | 'specialties' | 'ai-feedback';

const AdminPage: React.FC = (): React.ReactElement => {
  const navigate = useNavigate();
  const currentUser = useSelector((state: RootState) => state.user);
  const { t } = useI18n();
  const { enqueueSnackbar } = useSnackbar();

  // Состояния
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [showDialog, setShowDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
  const [activeTab, setActiveTab] = useState<AdminTab>('users');

  // Фильтры и пагинация
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [organizationFilter, setOrganizationFilter] = useState('');
  const [activationFilter, setActivationFilter] = useState<string>('');
  const [limit, setLimit] = useState(50);
  const [offset, setOffset] = useState(0);

  // Уникальные значения для фильтров
  const [organizations, setOrganizations] = useState<string[]>([]);
  const [showFiltersSheet, setShowFiltersSheet] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState<Set<number>>(() => new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const selectAllCheckboxRef = useRef<HTMLInputElement>(null);

  const currentUserId = currentUser?.id ?? 0;
  /** Тарифы, организации и специальности — только супер-администратор (role_id === 4). */
  const isSuperAdmin = currentUser?.role_id === 4;
  const canAccessAdmin =
    currentUser?.role_id === 3 || currentUser?.role_id === 4;

  // Проверка прав доступа: админ-панель для руководителя (3) и супер-админа (4)
  useEffect(() => {
    if (currentUser && !canAccessAdmin) {
      navigate('/dialogs');
    }
  }, [currentUser, canAccessAdmin, navigate]);

  useEffect(() => {
    if (
      !isSuperAdmin &&
      (activeTab === 'tariffs' || activeTab === 'organizations' || activeTab === 'specialties')
    ) {
      setActiveTab('users');
    }
  }, [isSuperAdmin, activeTab]);

  // Загрузка пользователей
  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params: any = {
        limit,
        offset,
      };

      if (search.trim()) params.search = search.trim();
      if (roleFilter) params.role_id = parseInt(roleFilter);
      if (organizationFilter) params.organization = organizationFilter;
      if (activationFilter) params.is_active = activationFilter === 'true';

      const response: UserListResponse = await getUsersList(params);
      setUsers(response.users);
      setTotal(response.total);

      // Обновляем списки для фильтров
      const uniqueOrgs = [...new Set(response.users.map(u => u.organization).filter(Boolean))];
      setOrganizations(uniqueOrgs);

      // Отладочный вывод для проверки наличия специальностей
      if (response.users.length > 0) {
        console.log('Загружены пользователи с специальностями:', response.users.map(u => ({
          id: u.id,
          name: `${u.first_name} ${u.last_name}`,
          hasSpecialties: !!u.specialties,
          specialtiesCount: u.specialties?.length || 0,
          specialties: u.specialties,
          specialty: u.specialty
        })));
      }

    } catch (err) {
      setError(err instanceof Error ? err.message : t('admin.error.loadUsers'));
    } finally {
      setLoading(false);
    }
  }, [limit, offset, search, roleFilter, organizationFilter, activationFilter, t]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Сброс пагинации при изменении фильтров
  useEffect(() => {
    setOffset(0);
  }, [search, roleFilter, organizationFilter, activationFilter]);

  useEffect(() => {
    setSelectedUserIds(new Set());
  }, [offset, search, roleFilter, organizationFilter, activationFilter, limit]);

  const selectableUsersOnPage = useMemo(
    () => users.filter((u) => u.id !== currentUserId),
    [users, currentUserId]
  );

  const selectedOnPageCount = useMemo(() => {
    return selectableUsersOnPage.filter((u) => selectedUserIds.has(u.id)).length;
  }, [selectableUsersOnPage, selectedUserIds]);

  const allOnPageSelected =
    selectableUsersOnPage.length > 0 && selectedOnPageCount === selectableUsersOnPage.length;
  const someOnPageSelected = selectedOnPageCount > 0 && !allOnPageSelected;

  useEffect(() => {
    if (selectAllCheckboxRef.current) {
      selectAllCheckboxRef.current.indeterminate = someOnPageSelected;
    }
  }, [someOnPageSelected]);

  const toggleUserSelected = (userId: number) => {
    if (userId === currentUserId) return;
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const toggleSelectAllOnPage = () => {
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      const ids = selectableUsersOnPage.map((u) => u.id);
      if (allOnPageSelected) {
        ids.forEach((id) => next.delete(id));
      } else {
        ids.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const handleBulkDelete = async () => {
    const ids = [...selectedUserIds];
    if (ids.length === 0 || bulkDeleting) return;

    const confirmText = t('admin.users.bulkDeleteConfirm').replace('{count}', String(ids.length));
    if (!window.confirm(confirmText)) return;

    setBulkDeleting(true);
    try {
      const results = await Promise.allSettled(ids.map((id) => deleteUser(id)));
      const failed = results.filter((r) => r.status === 'rejected') as PromiseRejectedResult[];
      setSelectedUserIds(new Set());
      await fetchUsers();

      if (failed.length === 0) {
        enqueueSnackbar(
          t('admin.users.bulkDeleteSuccess').replace('{count}', String(ids.length)),
          { variant: 'success' }
        );
      } else {
        const firstMsg =
          failed[0].reason instanceof Error ? failed[0].reason.message : t('admin.users.bulkDeleteError');
        const summary = t('admin.users.bulkDeletePartialError')
          .replace('{failed}', String(failed.length))
          .replace('{total}', String(ids.length));
        enqueueSnackbar(`${summary}. ${firstMsg}`, { variant: 'error' });
      }
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : t('admin.users.bulkDeleteError'), { variant: 'error' });
    } finally {
      setBulkDeleting(false);
    }
  };

  // Обработчики

  const handleCreateUser = () => {
    setSelectedUser(null);
    setDialogMode('create');
    setShowDialog(true);
  };

  const handleEditUser = (user: AdminUser) => {
    setSelectedUser(user);
    setDialogMode('edit');
    setShowDialog(true);
  };

  const handleCloseDialog = () => {
    setShowDialog(false);
    setSelectedUser(null);
  };

  const handleUserUpdated = (updatedUser?: AdminUser | null) => {
    if (updatedUser) {
      setUsers(prevUsers =>
        prevUsers.map(u => (u.id === updatedUser.id ? { ...u, ...updatedUser } : u))
      );
      setSelectedUser(prev =>
        prev && prev.id === updatedUser.id ? { ...prev, ...updatedUser } : prev
      );
      handleCloseDialog();
      return;
    }

    // Fallback для случаев, когда коллбек вызывается без данных (например, после удаления/создания)
    fetchUsers();
    handleCloseDialog();
  };

  const handleActivationStatusChange = async (userId: number, currentStatus: boolean) => {
    try {
      const updatedUser = await updateUserActivationStatus(userId, !currentStatus);
      // Обновляем локальное состояние с данными от сервера
      setUsers(prevUsers =>
        prevUsers.map(user =>
          user.id === userId
            ? { ...user, ...updatedUser }
            : user
        )
      );
    } catch (error) {
      console.error('Ошибка обновления статуса активации:', error);
      // Можно добавить уведомление об ошибке
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  if (error) {
    return (
      <div className="page-container">
        <div className="admin-header">
          <div className="header-left">

            <h1>{t('admin.title')}</h1>
          </div>
        </div>
        <div className="page-content">
          <div className="error-message">
            <p>{error}</p>
            <button onClick={fetchUsers}>{t('admin.tryAgain')}</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="admin-header">
        <div className="header-left">
          <h1>{t('admin.title')}</h1>
        </div>

      </div>

      <div className="page-content admin-page">
        <div className="admin-tabs">
          <button
            type="button"
            className={`admin-tab-button ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => setActiveTab('users')}
          >
            {t('admin.tab.users')}
          </button>
          {isSuperAdmin && (
            <>
              <button
                type="button"
                className={`admin-tab-button ${activeTab === 'tariffs' ? 'active' : ''}`}
                onClick={() => setActiveTab('tariffs')}
              >
                {t('admin.tab.tariffs')}
              </button>
              <button
                type="button"
                className={`admin-tab-button ${activeTab === 'organizations' ? 'active' : ''}`}
                onClick={() => setActiveTab('organizations')}
              >
                {t('admin.tab.organizations')}
              </button>
              <button
                type="button"
                className={`admin-tab-button ${activeTab === 'specialties' ? 'active' : ''}`}
                onClick={() => setActiveTab('specialties')}
              >
                {t('admin.tab.specialties')}
              </button>
            </>
          )}
          <button
            type="button"
            className={`admin-tab-button ${activeTab === 'ai-feedback' ? 'active' : ''}`}
            onClick={() => setActiveTab('ai-feedback')}
          >
            {t('admin.tab.aiFeedback')}
          </button>
        </div >

        {activeTab === 'users' && (
          <div className="users-section">
            <div className="section-header">
              <h2>{t('admin.users.title')}</h2>
              {activeTab === 'users' && (
                <div className="actions">
                  {selectedUserIds.size > 0 && (
                    <button
                      type="button"
                      className="danger-btn"
                      disabled={bulkDeleting}
                      onClick={handleBulkDelete}
                    >
                      {t('admin.users.bulkDelete')} ({selectedUserIds.size})
                    </button>
                  )}
                  <button type="button" className="button-primary" onClick={handleCreateUser}>
                    {t('admin.users.add')}
                  </button>
                </div>
              )}
            </div>
            <div className="users-controls">
              <div className="search-filters">
                <input
                  type="text"
                  placeholder={t('admin.search.placeholder')}
                  value={search}
                  onChange={handleSearchChange}
                  className="search-input"
                />

                {/* Кнопка фильтров */}
                <button
                  type="button"
                  className="filters-button-mobile"
                  onClick={() => setShowFiltersSheet(true)}
                >
                  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
                  </svg>
                  {t('admin.filters')}
                </button>

                <div className="filters-desktop">
                  <CustomSelect
                    value={roleFilter}
                    onChange={(value) => setRoleFilter(value.toString())}
                    className=""
                    placeholder={t('admin.filter.allRoles')}
                    options={[
                      { value: "", label: t('admin.filter.allRoles') },
                      ...Object.entries(ROLES).map(([id]) => ({
                        value: id,
                        label: t(`admin.role.${id}`)
                      }))
                    ]}
                  />

                  <CustomSelect
                    value={organizationFilter}
                    onChange={(value) => setOrganizationFilter(value.toString())}
                    className=""
                    placeholder={t('admin.filter.allOrganizations')}
                    options={[
                      { value: "", label: t('admin.filter.allOrganizations') },
                      ...organizations.map((org) => ({
                        value: org,
                        label: org
                      }))
                    ]}
                  />

                  <CustomSelect
                    value={activationFilter}
                    onChange={(value) => setActivationFilter(value.toString())}
                    className=""
                    placeholder={t('admin.filter.allStatuses')}
                    options={[
                      { value: "", label: t('admin.filter.allStatuses') },
                      { value: "true", label: t('admin.filter.active') },
                      { value: "false", label: t('admin.filter.inactive') }
                    ]}
                  />

                  <CustomSelect
                    value={limit}
                    onChange={(value) => setLimit(Number(value))}
                    className=""
                    placeholder={t('admin.filter.recordsCount')}
                    options={[
                      { value: 10, label: t('admin.filter.records10') },
                      { value: 25, label: t('admin.filter.records25') },
                      { value: 50, label: t('admin.filter.records50') },
                      { value: 100, label: t('admin.filter.records100') }
                    ]}
                  />
                </div>
              </div>
            </div>

            <FiltersBottomSheet
              isOpen={showFiltersSheet}
              onClose={() => setShowFiltersSheet(false)}
              title={t('admin.filters')}
            >
              <div className="filters-sheet-content">
                <div className="form-group">
                  <label>{t('admin.filter.role')}</label>
                  <CustomSelect
                    value={roleFilter}
                    onChange={(value) => setRoleFilter(value.toString())}
                    className=""
                    placeholder={t('admin.filter.allRoles')}
                    options={[
                      { value: "", label: t('admin.filter.allRoles') },
                      ...Object.entries(ROLES).map(([id]) => ({
                        value: id,
                        label: t(`admin.role.${id}`)
                      }))
                    ]}
                  />
                </div>

                <div className="form-group">
                  <label>{t('admin.filter.organization')}</label>
                  <CustomSelect
                    value={organizationFilter}
                    onChange={(value) => setOrganizationFilter(value.toString())}
                    className=""
                    placeholder={t('admin.filter.allOrganizations')}
                    options={[
                      { value: "", label: t('admin.filter.allOrganizations') },
                      ...organizations.map((org) => ({
                        value: org,
                        label: org
                      }))
                    ]}
                  />
                </div>

                <div className="form-group">
                  <label>{t('admin.filter.accountStatus')}</label>
                  <CustomSelect
                    value={activationFilter}
                    onChange={(value) => setActivationFilter(value.toString())}
                    className=""
                    placeholder={t('admin.filter.allStatuses')}
                    options={[
                      { value: "", label: t('admin.filter.allStatuses') },
                      { value: "true", label: t('admin.filter.active') },
                      { value: "false", label: t('admin.filter.inactive') }
                    ]}
                  />
                </div>

                <div className="form-group">
                  <label>{t('admin.filter.recordsCount')}</label>
                  <CustomSelect
                    value={limit}
                    onChange={(value) => setLimit(Number(value))}
                    className=""
                    placeholder={t('admin.filter.recordsCount')}
                    options={[
                      { value: 10, label: t('admin.filter.records10') },
                      { value: 25, label: t('admin.filter.records25') },
                      { value: 50, label: t('admin.filter.records50') },
                      { value: 100, label: t('admin.filter.records100') }
                    ]}
                  />
                </div>
              </div>
            </FiltersBottomSheet>

            {loading ? (
              <div className="loading">{t('admin.loading')}</div>
            ) : (
              <>
                <div className="users-table-container">
                  <table className="admin-table users-table">
                    <thead>
                      <tr>
                        <th className="user-select-cell" scope="col" aria-label={t('admin.table.select')}>
                          <input
                            ref={selectAllCheckboxRef}
                            type="checkbox"
                            checked={allOnPageSelected}
                            onChange={toggleSelectAllOnPage}
                            disabled={selectableUsersOnPage.length === 0 || bulkDeleting}
                            aria-label={t('admin.users.selectAllOnPage')}
                          />
                        </th>
                        <th>{t('admin.table.user')}</th>
                        <th>{t('admin.table.email')}</th>
                        <th>{t('admin.table.subscription')}</th>
                        <th>{t('admin.table.specialty')}</th>
                        <th>{t('admin.table.organization')}</th>
                        <th>{t('admin.table.role')}</th>
                        <th>{t('admin.table.accountStatus')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((user) => (
                        <tr
                          key={user.id}
                          onClick={() => handleEditUser(user)}
                          className={selectedUser?.id === user.id ? 'selected' : ''}
                        >
                          <td
                            className="user-select-cell"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <input
                              type="checkbox"
                              checked={user.id === currentUserId ? false : selectedUserIds.has(user.id)}
                              onChange={() => toggleUserSelected(user.id)}
                              disabled={bulkDeleting || user.id === currentUserId}
                              title={user.id === currentUserId ? t('admin.users.cannotDeleteSelf') : undefined}
                              aria-label={
                                user.id === currentUserId
                                  ? t('admin.users.cannotDeleteSelf')
                                  : t('admin.users.selectRow')
                              }
                            />
                          </td>
                          <td>
                            <span className="user-name">
                              {user.first_name} {user.last_name}
                            </span>
                          </td>
                          <td>{user.email}</td>
                          <td>{renderUserSubscriptionStatus(user, t)}</td>
                          <td>
                            {(() => {
                              // Проверяем наличие массива специальностей
                              if (user.specialties && Array.isArray(user.specialties) && user.specialties.length > 0) {
                                // Если это массив объектов Specialty, извлекаем name
                                const specialtyNames = user.specialties.map(s => {
                                  if (typeof s === 'object' && s !== null && 'name' in s) {
                                    return s.name;
                                  }
                                  return String(s);
                                });
                                return specialtyNames.join(', ');
                              }
                              // Fallback на старое поле specialty для обратной совместимости
                              if (user.specialty) {
                                return user.specialty;
                              }
                              return '—';
                            })()}
                          </td>
                          <td>{user.organization}</td>
                          <td>
                            {(() => {
                              const roleId = user.role?.id || user.role_id || 1;
                              const roleKey = roleId >= 1 && roleId <= 4 ? `admin.role.${roleId}` : 'admin.role.unknown';
                              return (
                                <span className={`role-badge ${getRoleBadgeClass(roleId)}`}>
                                  {t(roleKey)}
                                </span>
                              );
                            })()}
                          </td>
                          <td>
                            <button
                              className={`activation-status-btn ${user.is_active ? 'active' : 'inactive'}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleActivationStatusChange(user.id, user.is_active);
                              }}
                              title={user.is_active ? t('admin.activation.deactivate') : t('admin.activation.activate')}
                            >
                              {user.is_active ? t('admin.activation.active') : t('admin.activation.inactive')}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="pagination pt-3">
                  <button
                    onClick={() => setOffset(Math.max(0, offset - limit))}
                    disabled={offset === 0}
                    className="pagination-btn"
                  >
                    {t('admin.pagination.prev')}
                  </button>
                  <span className="pagination-info">
                    {t('admin.pagination.showingPrefix')} {offset + 1}-{Math.min(offset + limit, total)} {t('admin.pagination.showingSuffix')} {total}
                  </span>
                  <button
                    onClick={() => setOffset(offset + limit)}
                    disabled={offset + users.length >= total}
                    className="pagination-btn"
                  >
                    {t('admin.pagination.next')}
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {activeTab === 'tariffs' && isSuperAdmin && <TariffsManager />}

        {activeTab === 'organizations' && isSuperAdmin && <OrganizationsManager />}

        {activeTab === 'specialties' && isSuperAdmin && <SpecialtiesManager />}

        {activeTab === 'ai-feedback' && <AIFeedbackManager />}

        {
          showDialog && activeTab === 'users' && (
            <UserDialog
              mode={dialogMode}
              user={selectedUser}
              onClose={handleCloseDialog}
              onUserUpdated={handleUserUpdated}
            />
          )
        }
      </div >
    </div >
  );
};

export default AdminPage; 